import { useState, useCallback, useEffect } from 'react'
import { toast } from '../services/toastService'
import { sightingAPI } from '../services/api'
import { trackConfirmation } from '../utils/analytics'
import { useAsyncOperation } from './useAsyncOperation'
import { createLogger } from '../utils/logger'
import { useTranslation } from 'react-i18next'

const logger = createLogger('useConfirmations')

/**
 * Custom hook for managing sighting confirmations
 * Loads pending confirmations and handles confirmation submissions
 * 
 * @param {Object} user - The current user object
 * @param {Function} t - Translation function
 * @param {Function} onConfirmSuccess - Optional callback to execute after successful confirmation (e.g., to refetch data)
 * @param {Object} userLocation - Optional user location {lat, lng} for proximity-based filtering
 */
export function useConfirmations(user, tProp, onConfirmSuccess = null, userLocation = null, autoLoad = true) {
  const { t: tHook } = useTranslation()
  const t = tProp || tHook
  const [pendingConfirmations, setPendingConfirmations] = useState([])
  const [confirmingId, setConfirmingId] = useState(null)
  const { loading: loadingConfirmations, execute } = useAsyncOperation()

  const loadPendingConfirmations = useCallback(async () => {
    if (!user) {
      setPendingConfirmations([])
      return
    }

    try {
      // Include location parameters if available for proximity filtering
      const params = {}
      if (userLocation?.lat && userLocation?.lng) {
        params.latitude = userLocation.lat
        params.longitude = userLocation.lng
        params.radius = 50000 // 50km radius
      }
      
      const response = await execute(
        () => sightingAPI.getPendingConfirmations(params),
        { silent: true }
      )
      const sightings = response.data.sightings || []
      // Get only top 3 for compact view
      setPendingConfirmations(sightings.slice(0, 3))
    } catch (error) {
      logger.error('Failed to load pending confirmations', error)
      // Don't show error toast - this is a background operation
    }
  }, [user, userLocation, execute])

  const handleConfirmSighting = useCallback(async (sightingId, { image, notes }) => {
    if (!user) {
      toast.error(t('map.loginToConfirm'))
      return
    }

    if (!image) {
      toast.error(t('toast.error.imageRequiredConfirm'))
      return
    }

    setConfirmingId(sightingId)
    try {
      // Upload image first
      const formData = new FormData()
      formData.append('image', image)
      const uploadResponse = await execute(
        () => sightingAPI.uploadImage(formData),
        { silent: true }
      )
      const imageUrl = uploadResponse.data.url

      // Submit confirmation with image
      await execute(
        () => sightingAPI.confirmSightingWithImage(sightingId, {
          imageUrl,
          notes
        }),
        { successMessage: t('map.confirmationSuccess') || 'Confirmation submitted for review' }
      )
      
      // Remove confirmed sighting from pending list
      setPendingConfirmations(prev => prev.filter(s => s.id !== sightingId))
      
      // Track confirmation
      trackConfirmation(sightingId)
      
      // Call refetch callback if provided
      if (onConfirmSuccess) {
        onConfirmSuccess()
      }
    } catch (error) {
      logger.error('Failed to confirm sighting', error)
      if (error.response?.status === 409 || error.response?.status === 400) {
        toast.warning(t('map.alreadyConfirmed') || 'You have already confirmed this sighting')
        // Still remove from pending list
        setPendingConfirmations(prev => prev.filter(s => s.id !== sightingId))
      }
    } finally {
      setConfirmingId(null)
    }
  }, [user, t, onConfirmSuccess, execute])

  // Load pending confirmations when user or location changes (deferred when autoLoad=false)
  useEffect(() => {
    if (!autoLoad) return
    if (user) {
      loadPendingConfirmations()
    } else {
      setPendingConfirmations([])
    }
  }, [user, loadPendingConfirmations, autoLoad])

  return {
    pendingConfirmations,
    loadingConfirmations,
    confirmingId,
    loadPendingConfirmations,
    handleConfirmSighting
  }
}
